import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

interface ReportingMetricCardProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'teal' | 'amber';
  muted?: boolean;
  trailing?: ReactNode;
  footer?: ReactNode;
  emphasis?: 'subtle' | 'highlight';
  isLoading?: boolean;
  loadingLabel?: string;
  className?: string;
}

const dotTones: Record<
  NonNullable<ReportingMetricCardProps['tone']>,
  string
> = {
  default: 'bg-neutral-500',
  teal: 'bg-teal-400',
  amber: 'bg-amber-400',
};

export function ReportingMetricCard({
  label,
  value,
  hint,
  tone = 'default',
  muted = false,
  trailing,
  footer,
  emphasis: _emphasis = 'subtle',
  isLoading = false,
  loadingLabel,
  className,
}: ReportingMetricCardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-neutral-800/70 bg-neutral-900/40 p-4',
        muted && 'opacity-60',
        className,
      )}
      aria-busy={isLoading || undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
          <span className={cn('h-1.5 w-1.5 rounded-full', dotTones[tone])} />
          {label}
        </div>
        {trailing && (
          <div className="text-[11px] text-neutral-500">{trailing}</div>
        )}
      </div>

      {isLoading ? (
        <MetricCardSkeleton label={loadingLabel ?? `Loading ${label}`} />
      ) : (
        <>
          <div className="mt-2 text-2xl font-semibold tracking-tight text-white tabular-nums">
            {value}
          </div>

          {hint && (
            <div className="mt-1 text-xs leading-relaxed text-neutral-500">
              {hint}
            </div>
          )}

          {footer && <div className="mt-3">{footer}</div>}
        </>
      )}
    </div>
  );
}

function MetricCardSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="mt-2 space-y-2">
      <div className="h-8 w-24 animate-pulse rounded-md bg-neutral-800/80" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-800/70" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-800/50" />
      <div className="pt-1">
        <div className="h-1 w-full animate-pulse rounded-full bg-neutral-800/60" />
      </div>
    </div>
  );
}
